/**
 * Local calendar-day helpers.
 *
 * A calendar day is a *local* day. `new Date().toISOString().slice(0, 10)` is
 * the idiom this codebase reached for, and it is wrong everywhere east of
 * Greenwich: `toISOString` converts to UTC first, so in Harare (UTC+2) local
 * midnight on 1 October becomes 30 September 22:00 and the key comes back as
 * the previous day.
 *
 * On a month grid that mistake does not look like an off-by-one-day bug. 365
 * days is 52 weeks plus one day, so every date falls one weekday later each
 * year — and a grid shifted back by one day therefore draws every date on the
 * weekday it will occupy in the *following* year. October 2026 rendered as
 * October 2027. It also pushed the 31st off the end of the month.
 *
 * Use `isoDay` for anything that is a date rather than an instant: query
 * windows, grid keys, `<input type="date">` values, day buckets. Keep
 * `toISOString()` for timestamps, where UTC is the point.
 */

/** Local YYYY-MM-DD for a Date (or for today when called with no argument). */
export const isoDay = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Today, as a local YYYY-MM-DD. */
export const todayIso = () => isoDay();

/**
 * `n` days from `d`, by calendar date rather than by adding milliseconds, so a
 * daylight-saving change cannot land the result on the previous day.
 */
export const addDays = (d, n) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

/** First day of `d`'s month, local. */
export const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);

/** Last day of `d`'s month, local. Day 0 of the next month is the trick. */
export const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

/** Monday-based weekday index, 0 = Monday … 6 = Sunday. */
export const mondayIndex = (d) => (d.getDay() + 6) % 7;

/** Monday of `d`'s week, local. */
export const startOfWeek = (d) => addDays(d, -mondayIndex(d));

/** Inclusive list of local YYYY-MM-DD strings from `from` to `to`. */
export const isoDaysBetween = (from, to) => {
  const out = [];
  for (let cursor = new Date(from.getFullYear(), from.getMonth(), from.getDate());
       cursor <= to;
       cursor = addDays(cursor, 1)) {
    out.push(isoDay(cursor));
  }
  return out;
};

/**
 * Parse a YYYY-MM-DD as a LOCAL date. `new Date('2026-10-08')` is parsed as
 * UTC midnight by the spec, which renders as 7 October anywhere west of
 * Greenwich; this does not.
 */
export const parseIsoDay = (value) => {
  const [y, m, d] = String(value || '').slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
